import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import useTileControls from "../hooks/useTileControls";
import { getCallAPIProvider } from "services/CallAPIProvider";
import { Logger } from "utils/logger";
import {
  ccModerationUserJoined,
  ccStatusChanged,
  ccModerationUserLeft,
} from "store/actions/ccModeration";
import {
  getCCModerationProvider,
  setURL,
} from "../providers/CCModerationProvider";
import "./CCModeration.scss";
import useEventListener from "../hooks/useEventListener";
import {
  ccModeratorTurnOffCC,
  ccModeratorTurnOnCC,
} from "store/actions/googleAnalytics";
import { isCustomParamEnabled } from "utils/helpers";

const ccLogger = new Logger("CC moderation");

export default () => {
  const dispatch = useDispatch();
  const ccModerationSubscriptionToken = useRef(null);
  const config = useSelector((state) => state.config);
  const { listOfGCPServices } = useSelector((state) => state.config);
  const customParameters = config.customParameters;
  const participant = useSelector((state) => state.call.participants);
  const participantList = useRef([]);
  const ccModerationParticipant = useSelector(
    (state) => state.ccModeration?.ccModerationParticipants
  );
  const provider = useRef();
  const remoteParticipantsCapabilty = useRef(ccModerationParticipant);
  const sendEndpointDiscoveryRequest = useRef(false);
  const localParticpant = useRef(null);
  const RemoteParticpant = useRef([]);
  const isSubscribedCallIntiated = useRef(false);
  const user = useSelector((state) => state.user);
  const call = useSelector((state) => state.call);

  const isUserRegistered = user.isRegistered;
  const isUserAdmin = (user.accountType || "").toLowerCase() === "admin";
  const isCCModerationEnabled = useRef(false);

  const networkConnectionStatus = useSelector(
    (state) => state.app.networkConnectionStatus
  );
  const callReconnecting = useSelector((state) => state.app.callReconnecting);

  useTileControls();

  const triggerCustomEvent = (eventName, data) => {
    const event = new CustomEvent(eventName, { detail: data });
    document.dispatchEvent(event);
  };

  const subscribeForRemoteConfigServiceEvent = useCallback(
    async (localParticipantID, scope = "sender") => {
      let confrenceId = await getConfrenceID();
      try {
        if (user.isGuest) {
          ccLogger.logError("User is guest");
          return;
        }
        if (!customParameters) {
          ccLogger.logError("PortalCustomParameters is not available");
          return;
        }

        let isCCModerationDisabledInPortal = !isCustomParamEnabled(
          isUserRegistered
            ? customParameters?.isCCModerationFeatureSupported || false
            : false
        );
        if (isCCModerationDisabledInPortal) {
          isCCModerationEnabled.current = false;
          ccLogger.logError("Feature is disabled in custom parameters");
          return;
        }
        isCCModerationEnabled.current = true;
        const remoteConfigurationService =
          listOfGCPServices?.remoteConfigurationService || null;

        if (
          remoteConfigurationService === null ||
          !remoteConfigurationService?.url ||
          !remoteConfigurationService?.isServiceAvailable
        ) {
          ccLogger.logError(
            "listOfGCPServices remoteConfigurationService configuration is wrong"
          );
          return;
        }
        setURL(remoteConfigurationService.url);
        /* setURL(
          "https://rajesh-test-service-719903835271.us-central1.run.app/api/v1"
        ); */
      } catch (error) {
        ccLogger.logError("initialization failed", error);
      }
      const getTokenAndSubscribe = () => {
        if (!confrenceId && !localParticipantID) {
          ccLogger.logError(
            "no confrence id found  or no local particpant id found"
          );
          return;
        }
        provider.current = getCCModerationProvider();
        provider.current
          .getSubscriptionToken(confrenceId, localParticipantID, scope)
          .then((data) => {
            ccModerationSubscriptionToken.current =
              data.data.subscriptionToken || null;
            if (ccModerationSubscriptionToken.current) {
              provider.current
                .subscribeToTopic(
                  ccModerationSubscriptionToken.current,
                  handleCCModerationMessage,
                  statusCallback
                )
                .catch((e) =>
                  ccLogger.logError(
                    `Error while subscribe on sender`,
                    JSON.stringify(e)
                  )
                );
            } else {
              ccLogger.logInfo(`Can't get token for cc moderation sender`);
            }
          })
          .catch((e) => {
            ccLogger.logError(
              "Error while getting subscription token for cc moderation",
              JSON.stringify(e?.error)
            );
          });
      };

      const statusCallback = (status, message, code) => {
        ccLogger.logInfo(
          `Subscription on cc moderatrion. Status = ${status}, message = ${message}, code = ${code}`
        );

        if (status === "SUBSCRIPTION_SUBSCRIBED") {
          sendEndpointDiscoveryRequest.current = false;
          window.setTimeout(sendFeatureDiscoveryRequest, 10000);
        }

        if (status === "SUBSCRIPTION_INOPERATIVE") {
          ccModerationSubscriptionToken.current = null;
          getTokenAndSubscribe();
        } else if (status === "SUBSCRIPTION_ERROR") {
          ccModerationSubscriptionToken.current = null;
          getTokenAndSubscribe();
        }
      };

      const handleCCModerationMessage = (message) => {
        messageRecieved(message);
        triggerCustomEvent("roomConfigurationMessageRecieved", {
          detail: message,
        });
      };

      getTokenAndSubscribe();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const renderCCModerationButton = (participantId, capabilties) => {
    capabilties.forEach(function (capabilty) {
      if (capabilty.featureKey === "closed-captioning") {
        ccLogger.logInfo(
          `closed caption moderation ability  ${capabilty?.enabled}, current value = ${capabilty?.currentValue} , pID = ${participantId}`
        );
        const ccCapabilty = {
          isEnable: capabilty?.enabled || false,
          featureID: capabilty?.featureId || null,
          featurName: capabilty?.featureKey || "",
          optionType: capabilty?.options || null,
          currentValue: capabilty?.currentValue || "off",
          particpantId: participantId,
        };
        dispatch(ccModerationUserJoined(ccCapabilty));
      }
    });
  };

  useEffect(() => {
    remoteParticipantsCapabilty.current = ccModerationParticipant;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccModerationParticipant]);

  const setCCModerationValue = (participantId, value) => {
    let participant = remoteParticipantsCapabilty.current?.filter((item) => {
      return item.particpantId === participantId;
    });
    if (participant.length > 0) {
      participant[0].currentValue = value;
      dispatch(ccStatusChanged(participant[0]));
    }
  };

  const clearData = useCallback(() => {
    ccLogger.logInfo("Clear desktop component data");
    if (ccModerationSubscriptionToken.current) {
      provider.current?.unsubscribeFromTopic(
        ccModerationSubscriptionToken.current
      );
      ccModerationSubscriptionToken.current = null;
      sendEndpointDiscoveryRequest.current = false;
      provider.current = null;
      remoteParticipantsCapabilty.current = [];
      localParticpant.current = null;
      RemoteParticpant.current = [];
      isSubscribedCallIntiated.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendFeatureDiscoveryRequest = async () => {
    if (!sendEndpointDiscoveryRequest.current) {
      sendEndpointDiscoveryRequest.current = true;
      const confrenceId = await getConfrenceID();

      const remoteSCIPIds = [];

      participantList.current.map((data) => {
        remoteSCIPIds.push(getScipId(data.id));
        return data;
      });
      if (confrenceId && remoteSCIPIds.length > 0) {
        provider.current?.featureDiscovery(confrenceId, remoteSCIPIds);
        ccLogger.logInfo(`feature discovery request sent`);
      }
    }
  };

  const getScipId = (id) =>
    id?.substring?.(id.indexOf(":") + 1, id.indexOf(";"));

  const getConfrenceID = async () => {
    let confrenceId = null;
    const vidyoConnector =
      getCallAPIProvider().vidyoConnector || getCallAPIProvider().vidyoEndpoint;
    const statsJson = await vidyoConnector.GetStatsJson();
    const data = JSON.parse(statsJson);
    if (data.userStats[0].roomStats.length) {
      // filter not active rooms that for some reson may be present in statistic
      const filteredRoomsStats = data.userStats[0].roomStats.filter(
        (roomData) => {
          return (
            roomData && roomData.participantStats.length && roomData.callId
          );
        }
      );
      confrenceId = filteredRoomsStats.length
        ? filteredRoomsStats[0].conferenceId
        : null;
    }
    return confrenceId;
  };

  const sendCCModerationCommand = async (participant) => {
    const conferenceId = await getConfrenceID();
    const value = participant?.currentValue === "on" ? "off" : "on";
    const particpantId = participant?.particpantId;
    const featureId = participant?.featureID;
    const featureName = participant?.featurName;
    provider.current?.sendCommand(
      conferenceId,
      particpantId,
      featureId,
      featureName,
      value
    );
    if (value === "on") {
      ccLogger.logInfo(
        `send closed caption turn on command pID = ${particpantId}`
      );
      dispatch(ccModeratorTurnOnCC());
    } else if (value === "off") {
      ccLogger.logInfo(
        `send closed caption turn off command pID = ${particpantId}`
      );
      dispatch(ccModeratorTurnOffCC());
    }
    console.log(
      `User clicks on cc moderation button for `,
      conferenceId,
      value,
      particpantId,
      featureId,
      featureName,
      value
    );
  };
  const messageRecieved = (message) => {
    if(!isCCModerationEnabled.current){
      ccLogger.logInfo("cc moderation is disabled");
      return;
    }
    console.log(`on roomConfigurationMessageRecieved button for `, message);
    const MessageContent = JSON.parse(message || "");
    const data = MessageContent?.data || null;
    ccLogger.logInfo(
      `recieved message from event server ${MessageContent.eventType}, pID = ${data?.participantScipId}`
    );
    if (MessageContent.eventType === "remote-feature-register-request") {
      const remoteParticipantId = data.participantScipId;
      const capabilties = data.remoteCapabilities;
      renderCCModerationButton(remoteParticipantId, capabilties);
    }
    if (MessageContent.eventType === "remote-feature-register-request-global") {
      const remoteFeatures = data.remoteFeatures;
      remoteFeatures.forEach(function (feature) {
        const remoteParticipantId = feature.participantScipId;
        const capabilties = feature.remoteCapabilities;
        renderCCModerationButton(remoteParticipantId, capabilties);
      });
    }
    if (
      MessageContent.eventType === "remote-feature-status-request" &&
      data.featureKey === "closed-captioning"
    ) {
      const remoteParticipantId = data.participantScipId;
      const value = data.value;
      setCCModerationValue(remoteParticipantId, value);
    }
  };

  useEventListener("ccModerationClicked", ({ detail }) => {
    sendCCModerationCommand(detail);
  });

  useEventListener("roomConfigurationMessageRecieved", ({ detail }) => {
    console.log(`on roomConfigurationMessageRecieved button for `, detail);
    messageRecieved(detail);
  });

  useEffect(() => {
    ccLogger.logInfo("Component initialized");
    return () => {
      ccLogger.logInfo("Component UnInitialized");
      clearData();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (participant?.list.length > 0 && !isSubscribedCallIntiated.current) {
      for (let particpantData of participant.list) {
        const isUserRoomOwner =
          user.userInfo?.entityID &&
          call.roomInfo?.ownerID &&
          user.userInfo?.entityID === call.roomInfo?.ownerID;
        if (
          particpantData.isLocal &&
          isUserRegistered &&
          (isUserRoomOwner || isUserAdmin || user.becomeModerator)
        ) {
          isSubscribedCallIntiated.current = true;
          subscribeForRemoteConfigServiceEvent(getScipId(particpantData.id));
          window.setTimeout(sendFeatureDiscoveryRequest, 6000);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant, call.roomInfo?.ownerID, user.becomeModerator]);

  useEffect(() => {
    participantList.current = participant?.list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant]);

  useEffect(() => {
    if (participant?.participantLeft) {
      ccLogger.logInfo(
        `particpant leave the conference pID = ${participant.participantLeft.id}`
      );
      dispatch(ccModerationUserLeft(getScipId(participant.participantLeft.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant.participantLeft]);

  useEffect(() => {
    if (!networkConnectionStatus || callReconnecting) {
      isSubscribedCallIntiated.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkConnectionStatus, callReconnecting]);

  return <div id="cc-moderation-component"></div>;
};
